export interface AIModelOption {
  id: string;
  name: string;
  provider: string;
  badge: string;
  category: 'llm' | 'search' | 'writing' | 'visual_video' | 'automation';
  categoryLabel: string;
  icon: string;
  accentColor: string;
  specialty: string;
  description: string;
  promptPersona: string;
}

export interface AIImageGeneratorOption {
  id: string;
  name: string;
  provider: string;
  badge: string;
  isFree: boolean;
  icon: string;
  accentColor: string;
  stylePreset: string;
  description: string;
  themeColors: [string, string, string];
  accentHex: string;
  promptSuffix: string;
  externalUrl?: string;
}

export interface AIQuickTaskOption {
  id:
    | 'full'
    | 'cover_image'
    | 'catchy_title'
    | 'description_details'
    | 'trending_tags'
    | 'seo_boost'
    | 'social_copy'
    | 'visual_script'
    | 'grammar_polish';
  label: string;
  shortLabel: string;
  icon: string;
  description: string;
}

export const AI_IMAGE_GENERATORS_CATALOG: AIImageGeneratorOption[] = [
  {
    id: 'nano_banana',
    name: 'Nano Banana (Gemini Flash Image)',
    provider: 'Google DeepMind',
    badge: 'Gratuito • Ultra Rápido',
    isFree: true,
    icon: '🍌',
    accentColor: 'from-amber-400 to-yellow-600',
    stylePreset: 'Fotorrealismo Japonês & Iluminação de Estúdio Nativa',
    description: 'Motor oficial Nano Banana (gemini-3.1-flash-lite-image) para criar capas nítidas, vibrantes e contextualizadas.',
    themeColors: ['#0f172a', '#78350f', '#f59e0b'],
    accentHex: '#fbbf24',
    promptSuffix: 'rendered with Nano Banana Gemini Flash Image engine, ultra crisp Japanese photography, natural studio lighting, 8k',
    externalUrl: 'https://aistudio.google.com/'
  },
  {
    id: 'chatgpt_image',
    name: 'ChatGPT (DALL·E 3 / GPT-4o Image)',
    provider: 'OpenAI',
    badge: 'Gratuito / Plus • Capas c/ Texto',
    isFree: true,
    icon: '🤖',
    accentColor: 'from-emerald-500 to-teal-700',
    stylePreset: 'Composição Editorial Inteligente & Alto Impacto',
    description: 'Cria capas de blog e anúncios com composição perfeita, fidelidade ao tema e elementos visuais persuasivos.',
    themeColors: ['#064e3b', '#0f172a', '#10b981'],
    accentHex: '#34d399',
    promptSuffix: 'created in OpenAI GPT-4o DALL-E 3 editorial style, hyper-detailed commercial composition, vibrant lighting',
    externalUrl: 'https://chatgpt.com/'
  },
  {
    id: 'canva_image',
    name: 'Canva Magic Media',
    provider: 'Canva',
    badge: 'Gratuito • Design de Capa',
    isFree: true,
    icon: '🖌️',
    accentColor: 'from-cyan-500 to-purple-600',
    stylePreset: 'Layout Gráfico Moderno, Thumbnail & Editorial',
    description: 'Ideal para capas de blog prontas para clique (alto CTR), estilo revista digital e vitrines de e-commerce.',
    themeColors: ['#1e1b4b', '#581c87', '#06b6d4'],
    accentHex: '#22d3ee',
    promptSuffix: 'Canva Magic Media editorial blog cover layout, bold graphic composition, clean modern typography space, vibrant colors',
    externalUrl: 'https://www.canva.com/ai-image-generator/'
  },
  {
    id: 'microsoft_designer',
    name: 'Microsoft Designer (Image Creator)',
    provider: 'Microsoft',
    badge: '100% Gratuito • DALL·E 3',
    isFree: true,
    icon: '💠',
    accentColor: 'from-sky-500 to-blue-700',
    stylePreset: 'Arte Digital 3D, Fotorrealismo & Contraste Premium',
    description: 'Ferramenta gratuita da Microsoft com motor DALL·E 3 para capas super chamativas, 3D e fotografia comercial.',
    themeColors: ['#0c4a6e', '#1e3a8a', '#38bdf8'],
    accentHex: '#38bdf8',
    promptSuffix: 'Microsoft Designer Image Creator style, ultra-sharp commercial visual, dynamic depth of field, rich lighting',
    externalUrl: 'https://designer.microsoft.com/image-creator'
  },
  {
    id: 'leonardo_ai',
    name: 'Leonardo.Ai (Phoenix / Kino XL)',
    provider: 'Leonardo AI',
    badge: 'Gratuito (Tokens Diários)',
    isFree: true,
    icon: '🦁',
    accentColor: 'from-purple-600 to-rose-600',
    stylePreset: 'Cinematográfico, Anime, Figures & Cultura Pop JP',
    description: 'Perfeito para capas de colecionáveis, figures, games, estética de Tóquio/Akihabara e iluminação dramática.',
    themeColors: ['#2e1065', '#4c0519', '#e11d48'],
    accentHex: '#fb7185',
    promptSuffix: 'Leonardo.Ai Phoenix & Kino XL preset, cinematic Tokyo neon rim lighting, unreal engine 5 detail, masterpiece 8k',
    externalUrl: 'https://app.leonardo.ai/'
  },
  {
    id: 'miora_ai',
    name: 'Miora AI Studio',
    provider: 'Miora AI',
    badge: 'Gratuito • Estética Asiática',
    isFree: true,
    icon: '🌸',
    accentColor: 'from-pink-500 to-rose-600',
    stylePreset: 'Estética Japonesa, J-Beauty, Lifestyle & Harmonia',
    description: 'Especializado em direção de arte limpa, estética japonesa moderna, cosméticos J-Beauty e fotografia suave.',
    themeColors: ['#500724', '#1f2937', '#f472b6'],
    accentHex: '#f472b6',
    promptSuffix: 'Miora AI aesthetic studio, authentic Japanese lifestyle photography, soft sakura bokeh, refined pastel and crimson tones',
    externalUrl: 'https://miora.ai/'
  },
  {
    id: 'seedream_ai',
    name: 'SeeDream AI (ByteDance Dreamina)',
    provider: 'ByteDance',
    badge: 'Gratuito • Alta Criatividade',
    isFree: true,
    icon: '🌊',
    accentColor: 'from-blue-500 to-indigo-600',
    stylePreset: 'Cores Vibrantes, Mercado Asiático & Composição Viral',
    description: 'Modelo avançado da ByteDance (SeeDream 3.0) com foco em tendências asiáticas, alto impacto visual e nitidez.',
    themeColors: ['#0f172a', '#1e3a8a', '#6366f1'],
    accentHex: '#818cf8',
    promptSuffix: 'SeeDream 3.0 ByteDance AI generation, ultra-vibrant Asian e-commerce photography, crystal clear textures, high contrast',
    externalUrl: 'https://dreamina.capcut.com/'
  },
  {
    id: 'magnific_ai',
    name: 'Magnific AI (Ultra HD 8K)',
    provider: 'Magnific',
    badge: 'Hiper-Realismo & Texturas',
    isFree: true,
    icon: '🔍',
    accentColor: 'from-amber-600 to-red-600',
    stylePreset: 'Macro Detalhamento 8K, Nitidez Extrema & Luxo',
    description: 'Gera e aprimora capas com nível máximo de nitidez, micro-texturas de produtos japoneses e luz de estúdio.',
    themeColors: ['#18181b', '#451a03', '#f97316'],
    accentHex: '#fb923c',
    promptSuffix: 'Magnific AI 8K upscaled hyper-detailed photography, macro studio sharpness, raytraced reflections, Hasselblad H6D',
    externalUrl: 'https://magnific.ai/'
  },
  {
    id: 'midjourney',
    name: 'Midjourney v6.1 Studio',
    provider: 'Midjourney',
    badge: 'Fotografia Editorial Pro',
    isFree: false,
    icon: '🎨',
    accentColor: 'from-purple-600 to-pink-600',
    stylePreset: 'Fotografia Premiada, Iluminação Dramática & Arte',
    description: 'Padrão ouro em estética fotográfica, profundidade de campo e direção de arte cinematográfica.',
    themeColors: ['#090d16', '#3b0764', '#c084fc'],
    accentHex: '#c084fc',
    promptSuffix: 'editorial photography --ar 16:9 --v 6.1 --style raw --q 2, Tokyo aesthetic, award-winning commercial shot',
    externalUrl: 'https://www.midjourney.com/'
  },
  {
    id: 'flux_ai',
    name: 'Flux.1 Schnell / Dev',
    provider: 'Black Forest Labs',
    badge: '100% Gratuito • Open Weight',
    isFree: true,
    icon: '⚡',
    accentColor: 'from-emerald-600 to-cyan-600',
    stylePreset: 'Realismo Fotográfico Natural & Textos Nítidos',
    description: 'Um dos melhores geradores gratuitos da atualidade para fotos realistas de produtos e cenários do Japão.',
    themeColors: ['#042f2e', '#0f172a', '#14b8a6'],
    accentHex: '#2dd4bf',
    promptSuffix: 'Flux.1 Dev photorealistic render, authentic Tokyo Japan atmosphere, natural optical lens, ultra detailed',
    externalUrl: 'https://blackforestlabs.ai/'
  },
  {
    id: 'ideogram_ai',
    name: 'Ideogram 2.0',
    provider: 'Ideogram',
    badge: 'Gratuito • Capas com Tipografia',
    isFree: true,
    icon: '🔤',
    accentColor: 'from-orange-500 to-rose-600',
    stylePreset: 'Capas com Títulos Integrados, Pôsteres & Banners',
    description: 'Especialista gratuito em renderizar textos, selos e títulos perfeitos diretamente dentro da imagem de capa.',
    themeColors: ['#431407', '#1e1b4b', '#f97316'],
    accentHex: '#fb923c',
    promptSuffix: 'Ideogram 2.0 graphic cover design, crisp typography integration, modern Japanese poster aesthetic',
    externalUrl: 'https://ideogram.ai/'
  },
  {
    id: 'seaart_ai',
    name: 'SeaArt AI (Anime & Otaku JP)',
    provider: 'SeaArt',
    badge: '100% Gratuito • Cultura Pop JP',
    isFree: true,
    icon: '🎌',
    accentColor: 'from-red-600 to-rose-500',
    stylePreset: 'Anime, Mangá, Pokémon TCG, Action Figures & Akihabara',
    description: 'Ferramenta gratuita favorita para capas de colecionáveis, cartas Pokémon, animes e cultura pop japonesa.',
    themeColors: ['#450a0a', '#1e1b4b', '#ef4444'],
    accentHex: '#f87171',
    promptSuffix: 'SeaArt AI Japanese pop culture & Akihabara collector showcase, vibrant anime & figure studio lighting, 8k',
    externalUrl: 'https://www.seaart.ai/'
  },
  {
    id: 'playground_ai',
    name: 'Playground AI v3',
    provider: 'Playground',
    badge: 'Gratuito • E-commerce & Social',
    isFree: true,
    icon: '🎠',
    accentColor: 'from-violet-600 to-fuchsia-600',
    stylePreset: 'Design Vibrante para Vitrines, Blogs & Instagram',
    description: 'Geração gratuita rápida com presets prontos para capas de blog, mockups de produtos e redes sociais.',
    themeColors: ['#2e1065', '#4a044e', '#d946ef'],
    accentHex: '#e879f9',
    promptSuffix: 'Playground v3 graphic & photographic blend, high-conversion e-commerce showcase, vivid color grading',
    externalUrl: 'https://playground.com/'
  },
  {
    id: 'recraft_ai',
    name: 'Recraft V3 (Design & Vetor)',
    provider: 'Recraft AI',
    badge: 'Gratuito • #1 em Design',
    isFree: true,
    icon: '📐',
    accentColor: 'from-indigo-600 to-blue-500',
    stylePreset: 'Ilustração Editorial, Ícones 3D & Design de Marca',
    description: 'Líder em geração de imagens gráficas, ilustrações editoriais modernas e composições publicitárias.',
    themeColors: ['#1e1b4b', '#172554', '#60a5fa'],
    accentHex: '#60a5fa',
    promptSuffix: 'Recraft V3 professional brand design, editorial vector & 3D hybrid illustration, clean composition',
    externalUrl: 'https://www.recraft.ai/'
  },
  {
    id: 'firefly_ai',
    name: 'Adobe Firefly 3',
    provider: 'Adobe',
    badge: 'Gratuito (Créditos) • Comercial',
    isFree: true,
    icon: '🔥',
    accentColor: 'from-red-500 to-amber-500',
    stylePreset: 'Fotografia Comercial Segura & Luz de Estúdio',
    description: 'Motor criativo da Adobe com iluminação de estúdio impecável e cores comerciais equilibradas.',
    themeColors: ['#3f1d0b', '#1c1917', '#f59e0b'],
    accentHex: '#fbbf24',
    promptSuffix: 'Adobe Firefly Image 3 commercial studio photography, balanced key light, ultra clean background',
    externalUrl: 'https://firefly.adobe.com/'
  },
  {
    id: 'bing_creator',
    name: 'Bing Image Creator',
    provider: 'Microsoft Bing',
    badge: '100% Gratuito • Ilimitado',
    isFree: true,
    icon: '🌐',
    accentColor: 'from-teal-500 to-blue-600',
    stylePreset: 'Criação Instantânea Gratuita para Capas de Artigos',
    description: 'Gerador gratuito do Bing para criar capas criativas de artigos e produtos japoneses em segundos.',
    themeColors: ['#134e4a', '#1e3a8a', '#2dd4bf'],
    accentHex: '#2dd4bf',
    promptSuffix: 'Bing Image Creator DALL-E 3 HD cover, vivid Japanese export theme, sharp details',
    externalUrl: 'https://www.bing.com/images/create'
  }
];

export const AI_MODELS_CATALOG: AIModelOption[] = [
  {
    id: 'gemini',
    name: 'Google Gemini 3.8 Flash / Pro',
    provider: 'Google DeepMind',
    badge: 'Padrão • Google Search',
    category: 'search',
    categoryLabel: 'Pesquisa & Multimodal',
    icon: '✨',
    accentColor: 'from-blue-600 to-indigo-600',
    specialty: 'Pesquisa Google em Tempo Real, SEO & Copywriting Completo',
    description: 'Conecta ao Google Search para buscar preços reais em JPY, especificações técnicas, tendências de busca e SEO para o Google.',
    promptPersona: 'Atue como o Google Gemini 3.8 com Google Search Grounding ativado. Priorize dados factuais verificados da web japonesa e brasileira, SEO semântico para Google Shopping/Discover, precisão técnica e estrutura rica.'
  },
  {
    id: 'nano_banana',
    name: 'Nano Banana (Gemini Flash Image)',
    provider: 'Google DeepMind',
    badge: 'Gratuito • Capa & Imagem IA',
    category: 'visual_video',
    categoryLabel: 'Design, Foto & Vídeo',
    icon: '🍌',
    accentColor: 'from-amber-400 to-yellow-600',
    specialty: 'Geração de Capas de Blog, Fotos de Produto & Prompts Visuais',
    description: 'Especializado em criar imagens de capa vibrantes (16:9 e 1:1), direção visual japonesa e descrições ricas.',
    promptPersona: 'Atue como o motor visual Nano Banana do Google DeepMind. Crie títulos e conteúdos visualmente impactantes e gere imagens de capa de altíssima nitidez.'
  },
  {
    id: 'chatgpt',
    name: 'ChatGPT (GPT-4o / DALL·E 3)',
    provider: 'OpenAI',
    badge: 'Alta Conversão & Capa',
    category: 'llm',
    categoryLabel: 'LLMs Conversacionais',
    icon: '🤖',
    accentColor: 'from-emerald-600 to-teal-600',
    specialty: 'Títulos Chamativos, Copywriting Persuasivo & Capas DALL·E 3',
    description: 'Ideal para criar títulos magnéticos de alto CTR, descrições persuasivas de vendas, capas e estrutura AIDA.',
    promptPersona: 'Atue como o ChatGPT (GPT-4o) especialista em Direct Response Copywriting e E-commerce. Use gatilhos mentais de exclusividade, prova social e desejo, criando títulos magnéticos e textos ultra persuasivos.'
  },
  {
    id: 'claude',
    name: 'Claude 3.7 Sonnet',
    provider: 'Anthropic',
    badge: 'Editorial Premium',
    category: 'writing',
    categoryLabel: 'Redação & Copywriting',
    icon: '🧠',
    accentColor: 'from-amber-600 to-orange-600',
    specialty: 'Redação Editorial Profunda, Storytelling & Detalhamento',
    description: 'Excelente para textos naturais, fluidos, elegantes e artigos aprofundados sem parecer texto robótico, valorizando a cultura japonesa.',
    promptPersona: 'Atue como o Claude 3.7 Sonnet da Anthropic. Escreva com estilo editorial sofisticado, humano, empático e extremamente detalhado, destacando a tradição, precisão e autenticidade japonesa.'
  },
  {
    id: 'copilot',
    name: 'Microsoft Copilot',
    provider: 'Microsoft',
    badge: 'Produtividade & B2B',
    category: 'llm',
    categoryLabel: 'LLMs Conversacionais',
    icon: '💠',
    accentColor: 'from-sky-600 to-blue-700',
    specialty: 'Estrutura Corporativa, Tabelas Comparativas & Clareza',
    description: 'Focado em organização executiva, tabelas comparativas de custos (Japão vs Brasil), ficha técnica estruturada e síntese objetiva.',
    promptPersona: 'Atue como o Microsoft Copilot Enterprise. Estruture as informações com máxima clareza executiva, tabelas comparativas precisas (JPY vs BRL), bullet points objetivos e análise prática de custo-benefício.'
  },
  {
    id: 'microsoft_designer',
    name: 'Microsoft Designer AI',
    provider: 'Microsoft',
    badge: 'Gratuito • Capas & Artes',
    category: 'visual_video',
    categoryLabel: 'Design, Foto & Vídeo',
    icon: '🎨',
    accentColor: 'from-sky-500 to-indigo-600',
    specialty: 'Criação de Capas Gratuitas (DALL·E 3), Banners & Vitrines',
    description: 'Gera artes de capa gratuitas com estilo moderno, 3D ou fotorrealista e chamadas visuais para o blog e loja.',
    promptPersona: 'Atue como o Microsoft Designer AI. Crie títulos visuais de alto impacto, direção de arte moderna para capas e descrições comerciais atraentes.'
  },
  {
    id: 'canva',
    name: 'Canva Magic Write & Media',
    provider: 'Canva',
    badge: 'Gratuito • Capas & Cards',
    category: 'visual_video',
    categoryLabel: 'Design, Foto & Vídeo',
    icon: '🖌️',
    accentColor: 'from-cyan-500 to-purple-600',
    specialty: 'Capas de Blog, Frases de Impacto, Carrossel & Vitrines',
    description: 'Perfeito para criar capas chamativas gratuitas, títulos super atrativos e destaques prontos para vitrines.',
    promptPersona: 'Atue como o Canva Magic Studio. Crie títulos super atraentes, subtítulos visuais, destaques em tópicos curtos de fácil leitura e capas criativas.'
  },
  {
    id: 'leonardo_ai',
    name: 'Leonardo.Ai (Phoenix XL)',
    provider: 'Leonardo AI',
    badge: 'Gratuito • Anime & Figures',
    category: 'visual_video',
    categoryLabel: 'Design, Foto & Vídeo',
    icon: '🦁',
    accentColor: 'from-purple-600 to-rose-600',
    specialty: 'Capas Cinematográficas, Cultura Pop JP, Anime & Colecionáveis',
    description: 'Excelente para criar imagens de capa gratuitas de figures, games, Pokémon TCG e cenários de Tóquio/Akihabara.',
    promptPersona: 'Atue como o diretor visual do Leonardo.Ai Phoenix. Destaque a estética japonesa, colecionáveis, iluminação neon de Tóquio e riqueza visual.'
  },
  {
    id: 'miora_ai',
    name: 'Miora AI Studio',
    provider: 'Miora AI',
    badge: 'Gratuito • Estética JP',
    category: 'visual_video',
    categoryLabel: 'Design, Foto & Vídeo',
    icon: '🌸',
    accentColor: 'from-pink-500 to-rose-600',
    specialty: 'Capas Lifestyle Japonês, J-Beauty, Moda & Harmonia Visual',
    description: 'Focado em estética japonesa autêntica, cosméticos J-Beauty, moda de Tóquio e direção de arte elegante.',
    promptPersona: 'Atue como o Miora AI Creative Studio. Valorize a estética japonesa contemporânea, harmonia visual, J-Beauty e apresentação premium.'
  },
  {
    id: 'seedream_ai',
    name: 'SeeDream AI (ByteDance)',
    provider: 'ByteDance',
    badge: 'Gratuito • Criativo & Viral',
    category: 'visual_video',
    categoryLabel: 'Design, Foto & Vídeo',
    icon: '🌊',
    accentColor: 'from-blue-500 to-indigo-600',
    specialty: 'Capas de Alto CTR, Tendências Asiáticas & Visual Viral',
    description: 'Tecnologia criativa da ByteDance para gerar capas vibrantes e textos conectados às tendências asiáticas.',
    promptPersona: 'Atue como o SeeDream AI da ByteDance. Crie capas e conteúdos vibrantes, modernos e altamente clicáveis com foco no mercado asiático.'
  },
  {
    id: 'magnific_ai',
    name: 'Magnific AI (Ultra HD 8K)',
    provider: 'Magnific',
    badge: 'Ultra Detalhes 8K',
    category: 'visual_video',
    categoryLabel: 'Design, Foto & Vídeo',
    icon: '🔍',
    accentColor: 'from-amber-600 to-red-600',
    specialty: 'Capas Hiper-Realistas 8K, Nitidez Extrema & Macro Texturas',
    description: 'Ideal para destacar a qualidade de fabricação japonesa, texturas reais de produtos e fotografia macro.',
    promptPersona: 'Atue como o Magnific AI Studio. Descreva e gere visuais com hiper-realismo 8K, nitidez extrema de texturas e acabamento de luxo.'
  },
  {
    id: 'grok',
    name: 'Grok 3',
    provider: 'xAI',
    badge: 'Tendências Virais',
    category: 'search',
    categoryLabel: 'Pesquisa & Multimodal',
    icon: '🚀',
    accentColor: 'from-slate-700 to-zinc-900',
    specialty: 'Hashtags em Alta, Hype Otaku/Gamer & Tom Dinâmico',
    description: 'Captura o pulso das redes sociais, termos virais da comunidade colecionadora/gamer e cria chamadas ousadas.',
    promptPersona: 'Atue como o Grok 3 da xAI. Use um tom dinâmico, moderno, direto e conectado às tendências virais em tempo real da comunidade geek, otaku, colecionadores e importadores, incluindo as hashtags mais quentes.'
  },
  {
    id: 'deepseek',
    name: 'DeepSeek R1 / V3',
    provider: 'DeepSeek',
    badge: 'Raciocínio & Dados',
    category: 'llm',
    categoryLabel: 'LLMs Conversacionais',
    icon: '🐋',
    accentColor: 'from-indigo-600 to-blue-800',
    specialty: 'Análise Técnica, Precificação, Peso & SEO Analítico',
    description: 'Raciocínio lógico profundo para estimar especificações exatas, margem de economia na importação e palavras-chave Long Tail.',
    promptPersona: 'Atue como o DeepSeek R1 com raciocínio analítico profundo. Detalhe especificações técnicas minuciosas, cálculos claros de vantagem financeira na importação direta do Japão e arquitetura densa de palavras-chave SEO.'
  },
  {
    id: 'solar',
    name: 'Solar Pro (Upstage)',
    provider: 'Upstage AI',
    badge: 'Especialista Ásia/JP',
    category: 'llm',
    categoryLabel: 'LLMs Conversacionais',
    icon: '☀️',
    accentColor: 'from-orange-500 to-red-600',
    specialty: 'Mercado Asiático (JDM), Marcas Japonesas & Tradução Cultural',
    description: 'Otimizado para termos asiáticos, linhas exclusivas do Japão (Ichiban Kuji, Mercari JP, J-Beauty, Pokémon Center) e contexto JDM.',
    promptPersona: 'Atue como o modelo Solar Pro especialista em e-commerce asiático e produtos JDM (Japan Domestic Market). Destaque termos originais japoneses, linhas exclusivas de Tóquio/Akihabara e fidelidade cultural.'
  },
  {
    id: 'meta_ai',
    name: 'Meta AI (Llama 3.3)',
    provider: 'Meta',
    badge: 'Social & Engajamento',
    category: 'writing',
    categoryLabel: 'Redação & Copywriting',
    icon: '♾️',
    accentColor: 'from-blue-500 to-cyan-500',
    specialty: 'Copy para Instagram/Facebook/WhatsApp & Hashtags em Alta',
    description: 'Otimizado para engajamento social, compartilhamento no WhatsApp, chamadas para comunidades e hashtags de alto alcance.',
    promptPersona: 'Atue como o Meta AI (Llama 3.3) focado em Social Commerce. Crie títulos e descrições altamente compartilháveis, ideais para engajamento em redes sociais, grupos de WhatsApp e vitrines digitais.'
  },
  {
    id: 'perplexity',
    name: 'Perplexity AI Pro',
    provider: 'Perplexity',
    badge: 'Deep Web Research',
    category: 'search',
    categoryLabel: 'Pesquisa & Multimodal',
    icon: '🧭',
    accentColor: 'from-teal-600 to-emerald-700',
    specialty: 'Pesquisa Web Profunda, Comparativo de Preços & Fontes',
    description: 'Vasculha dados de mercado, histórico de lançamentos no Japão, raridade do item e responde às principais dúvidas dos compradores.',
    promptPersona: 'Atue como o Perplexity AI Pro. Realize uma síntese investigativa precisa com dados de mercado, raridade no Japão, faixa de preços nos marketplaces japoneses e respostas diretas às dúvidas frequentes.'
  },
  {
    id: 'notion_ai',
    name: 'Notion AI',
    provider: 'Notion',
    badge: 'Organização & Guias',
    category: 'writing',
    categoryLabel: 'Redação & Copywriting',
    icon: '📓',
    accentColor: 'from-neutral-700 to-stone-900',
    specialty: 'Checklists, Tabelas Limpas, Resumos & Passo a Passo',
    description: 'Transforma descrições e artigos em blocos perfeitamente escaneáveis com checklists, callouts e guias passo a passo.',
    promptPersona: 'Atue como o Notion AI. Organize todo o conteúdo de maneira impecavelmente escaneável, usando listas numeradas, checklists práticos, tabelas limpas e blocos de destaque fáceis de ler.'
  },
  {
    id: 'zapier',
    name: 'Zapier AI Central',
    provider: 'Zapier',
    badge: 'Funil & Automação',
    category: 'automation',
    categoryLabel: 'Automação & Funil',
    icon: '⚡',
    accentColor: 'from-orange-600 to-amber-600',
    specialty: 'CTAs de Conversão, Funil de Vendas & Gatilhos de Compra',
    description: 'Estrutura o anúncio ou post com foco em ação imediata: cadastro na Suíte, compra em 1 clique, alerta de estoque e cross-sell.',
    promptPersona: 'Atue como o Zapier AI especialista em automação de marketing e conversão de funil. Estruture o texto para guiar o leitor diretamente à ação (comprar para a Suíte em Saitama, consolidar pacotes e aproveitar o câmbio).'
  },
  {
    id: 'grammarly',
    name: 'Grammarly AI Business',
    provider: 'Grammarly',
    badge: 'Clareza & Polimento',
    category: 'writing',
    categoryLabel: 'Redação & Copywriting',
    icon: '✍️',
    accentColor: 'from-emerald-500 to-green-700',
    specialty: 'Gramática Impecável, Clareza, Fluidez & Tom Profissional',
    description: 'Garante um português do Brasil cristalino, persuasivo, livre de clichês ou erros, com vocabulário rico e leitura agradável.',
    promptPersona: 'Atue como o Grammarly AI Business. Produza e refine o texto com precisão gramatical absoluta em Português do Brasil, clareza máxima, ritmo de leitura envolvente e tom confiante e profissional.'
  },
  {
    id: 'midjourney',
    name: 'Midjourney v6.1 Studio',
    provider: 'Midjourney',
    badge: 'Direção Visual & Foto',
    category: 'visual_video',
    categoryLabel: 'Design, Foto & Vídeo',
    icon: '🎨',
    accentColor: 'from-purple-600 to-pink-600',
    specialty: 'Estética Visual, Descrição Sensorial & Prompts Fotográficos',
    description: 'Enriquece o anúncio ou post com descrições visuais ultra detalhadas (acabamento, pintura, texturas) e gera capas.',
    promptPersona: 'Atue como o diretor criativo do Midjourney v6.1. Valorize os detalhes visuais, acabamento estético, cores, texturas e apresentação de estúdio do produto, incluindo também um Prompt Visual detalhado.'
  },
  {
    id: 'runway',
    name: 'Runway Gen-3 Alpha',
    provider: 'Runway ML',
    badge: 'Vídeo & Unboxing',
    category: 'visual_video',
    categoryLabel: 'Design, Foto & Vídeo',
    icon: '🎬',
    accentColor: 'from-rose-600 to-red-700',
    specialty: 'Storytelling Dinâmico, Roteiro de Unboxing & Reels/TikTok',
    description: 'Cria descrições com ritmo cinematográfico e inclui roteiro rápido de cenas para vídeos de unboxing e Reels do produto.',
    promptPersona: 'Atue como o Runway Gen-3 AI Director. Crie um texto vibrante e visualmente dinâmico, adicionando uma seção especial de Roteiro de Unboxing / Vídeo Curto (Reels/TikTok) mostrando o produto em detalhes.'
  },
  {
    id: 'synthesia',
    name: 'Synthesia AI Studio',
    provider: 'Synthesia',
    badge: 'Pitch & Apresentação',
    category: 'visual_video',
    categoryLabel: 'Design, Foto & Vídeo',
    icon: '🗣️',
    accentColor: 'from-violet-600 to-indigo-700',
    specialty: 'Pitch de Vendas Falado, Apresentação Comercial & Demonstração',
    description: 'Cria textos com tom de apresentador especialista explicando cada vantagem do produto como em um vídeo demonstrativo.',
    promptPersona: 'Atue como o Synthesia AI Presenter. Escreva de forma envolvente, clara e didática como um apresentador especialista apresentando o produto ao cliente, incluindo um mini-pitch comercial.'
  },
  {
    id: 'jasper',
    name: 'Jasper AI (E-commerce)',
    provider: 'Jasper',
    badge: 'Boss Mode Copy',
    category: 'writing',
    categoryLabel: 'Redação & Copywriting',
    icon: '💎',
    accentColor: 'from-fuchsia-600 to-purple-700',
    specialty: 'Fórmula AIDA/PAS, Títulos Magnéticos & Alta Conversão',
    description: 'Especializado em e-commerce internacional, criando títulos irresistíveis, benefícios emocionais e argumentos racionais de compra.',
    promptPersona: 'Atue como o Jasper AI Boss Mode para E-commerce. Aplique as fórmulas AIDA (Atenção, Interesse, Desejo, Ação) e PAS, focando em conversão imediata e palavras-chave de intenção de compra.'
  },
  {
    id: 'mistral',
    name: 'Mistral Large 2',
    provider: 'Mistral AI',
    badge: 'Rápido & Preciso',
    category: 'llm',
    categoryLabel: 'LLMs Conversacionais',
    icon: '🌪️',
    accentColor: 'from-amber-500 to-red-600',
    specialty: 'Síntese Técnica Direta, Multilíngue & SEO Otimizado',
    description: 'Direto ao ponto, combinando ficha técnica enxuta, tradução precisa de termos japoneses e indexação semântica.',
    promptPersona: 'Atue como o Mistral Large 2. Seja extremamente preciso, elegante e direto, entregando informações técnicas densas, tradução exata de termos japoneses e tags de alta relevância.'
  }
];

export const AI_PRODUCT_QUICK_TASKS: AIQuickTaskOption[] = [
  {
    id: 'full',
    label: 'Gerar Anúncio Completo (Título + Capa + Descrição + Hashtags)',
    shortLabel: '✨ Gerar Anúncio Completo',
    icon: '✨',
    description: 'Preenche automaticamente título atraente, imagem de capa, categoria, peso, preço sugerido, descrição rica e hashtags em alta.'
  },
  {
    id: 'cover_image',
    label: 'Criar Imagem do Produto / Capa com IA (Nano Banana, Canva, Leonardo, etc.)',
    shortLabel: '🖼️ Criar Imagem / Capa IA',
    icon: '🖼️',
    description: 'Gera instantaneamente a imagem de capa do produto usando o estilo da ferramenta de IA escolhida.'
  },
  {
    id: 'catchy_title',
    label: 'Criar Títulos Atraentes e Chamativos (Alto CTR)',
    shortLabel: '🎯 Criar Título Chamativo',
    icon: '🎯',
    description: 'Gera um título magnético otimizado para vendas + 5 variações de títulos chamativos para você escolher com 1 clique.'
  },
  {
    id: 'description_details',
    label: 'Criar Descrição Persuasiva e Ficha Técnica Detalhada',
    shortLabel: '📝 Descrição & Detalhes',
    icon: '📝',
    description: 'Cria descrição completa com benefícios, especificações técnicas, conteúdo da caixa e comparativo Japão vs Brasil.'
  },
  {
    id: 'trending_tags',
    label: 'Adicionar Hashtags Virais e Palavras-Chave em Alta',
    shortLabel: '🔥 Hashtags & Palavras em Alta',
    icon: '🔥',
    description: 'Gera tags de alto volume de busca e hashtags em alta no mercado de importados e colecionáveis.'
  },
  {
    id: 'seo_boost',
    label: 'Otimizar SEO Google Shopping & Meta Description',
    shortLabel: '📈 SEO & Google Shopping',
    icon: '📈',
    description: 'Adiciona palavras-chave principais, Long Tail, termos relacionados e Meta Description otimizada.'
  },
  {
    id: 'social_copy',
    label: 'Gerar Copy para Instagram, TikTok e Grupos de WhatsApp',
    shortLabel: '📱 Copy Redes Sociais',
    icon: '📱',
    description: 'Cria uma chamada comercial pronta com emojis e hashtags para divulgar o produto nas redes sociais.'
  },
  {
    id: 'visual_script',
    label: 'Gerar Roteiro de Vídeo Unboxing & Prompt Fotográfico',
    shortLabel: '🎬 Roteiro Vídeo & Prompt',
    icon: '🎬',
    description: 'Cria roteiro curto para Reels/TikTok/Synthesia e prompt fotográfico estilo Midjourney/Canva.'
  },
  {
    id: 'grammar_polish',
    label: 'Revisar, Polir e Tornar Mais Persuasivo (Estilo Grammarly)',
    shortLabel: '✍️ Revisar & Polir Texto',
    icon: '✍️',
    description: 'Refina a gramática, clareza e poder de persuasão do título e descrição atuais.'
  }
];

export const AI_BLOG_QUICK_TASKS: AIQuickTaskOption[] = [
  {
    id: 'full',
    label: 'Gerar Post Completo (Título + Capa + Resumo + Artigo + Tags)',
    shortLabel: '✨ Gerar Post Completo',
    icon: '✨',
    description: 'Cria o artigo completo do blog com título atraente, imagem de capa, resumo, seções aprofundadas e palavras-chave em alta.'
  },
  {
    id: 'cover_image',
    label: 'Criar Imagem da Capa com IA (Nano Banana, ChatGPT, Canva, Leonardo, Miora, SeeDream, Magnific)',
    shortLabel: '🖼️ Criar Capa com IA',
    icon: '🖼️',
    description: 'Gera instantaneamente a imagem de capa 16:9 do artigo com o gerador de imagem de IA escolhido.'
  },
  {
    id: 'catchy_title',
    label: 'Criar Títulos Atraentes e Virais para o Blog',
    shortLabel: '🎯 Criar Título Chamativo',
    icon: '🎯',
    description: 'Gera um título de alto impacto + 5 opções de títulos atraentes para escolher com 1 clique.'
  },
  {
    id: 'description_details',
    label: 'Criar / Expandir Conteúdo Detalhado e Guia Passo a Passo',
    shortLabel: '📝 Conteúdo & Detalhes',
    icon: '📝',
    description: 'Desenvolve o conteúdo completo do post com subtítulos, tabelas comparativas e dicas práticas.'
  },
  {
    id: 'trending_tags',
    label: 'Adicionar Hashtags e Palavras-Chave em Alta (Trending SEO)',
    shortLabel: '🔥 Hashtags & Palavras em Alta',
    icon: '🔥',
    description: 'Pesquisa e adiciona as melhores tags, palavras-chave em alta e hashtags temáticas ao post.'
  },
  {
    id: 'seo_boost',
    label: 'Gerar Resumo (Excerpt) Magnético & Meta SEO Google Discover',
    shortLabel: '📈 Resumo & SEO Discover',
    icon: '📈',
    description: 'Cria um resumo instigante para os cards do blog e adiciona seção de perguntas frequentes (FAQ SEO) ao artigo.'
  },
  {
    id: 'visual_script',
    label: 'Gerar Roteiro de Vídeo (Reels/Shorts) & Prompt de Capa',
    shortLabel: '🎬 Roteiro Vídeo & Prompt Capa',
    icon: '🎬',
    description: 'Cria um roteiro de vídeo curto sobre o artigo + prompt visual para Midjourney/Canva/Leonardo.'
  },
  {
    id: 'grammar_polish',
    label: 'Revisar Gramática, Clareza e Escaneabilidade (Grammarly)',
    shortLabel: '✍️ Revisar & Polir Artigo',
    icon: '✍️',
    description: 'Melhora a fluidez, corrige erros e organiza o artigo em tópicos fáceis de ler.'
  }
];

export function getAIModelById(modelId?: string): AIModelOption {
  if (!modelId) return AI_MODELS_CATALOG[0];
  return (
    AI_MODELS_CATALOG.find((m) => m.id === modelId.toLowerCase()) ||
    AI_MODELS_CATALOG[0]
  );
}

export function getAIImageGeneratorById(generatorId?: string): AIImageGeneratorOption {
  if (!generatorId) return AI_IMAGE_GENERATORS_CATALOG[0];
  const clean = generatorId.toLowerCase();
  return (
    AI_IMAGE_GENERATORS_CATALOG.find(
      (g) => g.id === clean || g.id.startsWith(clean) || clean.startsWith(g.id.replace('_image', ''))
    ) || AI_IMAGE_GENERATORS_CATALOG[0]
  );
}
